<template>
  <div
    @dragover.prevent
    @dragleave.prevent="handleDragLeave()"
    @drop="dropHandler($event)"
    :class="{ 'draggable-box': hasDraggable }"
    class="white-box"
  >
    <div v-show="showSemiModal" class="generic-semimodal-container">
      <a-i-assistent
        v-if="newestChat"
        v-show="showAIAssistent"
        @suggestion-selected="handleWSSuggestion($event), handleGenericModal('ai')"
        @close="handleGenericModal('ai')"
        :dialogId="newestChat.id"
        :opportunityId="opportunity.id"
        semi-modal-mode
      />
    </div>

    <floating-open-new-room
      v-if="floatingOpenNewRoomState"
      @selectBot="selectBot($event)"
      @selectTemplate="selectTemplate($event)"
      @close="changeFloatingOpenNewRoomState(false)"
      :opportunity="opportunity"
      :preselected-bot-for-template="templateModeBot"
    />

    <floating-quick-messages
      v-if="floatingQuickMessagesState"
      @quickMessageSelected="getQuickMessageAndCloseOptions($event)"
      @quickFiledMessageSelected="sendMessageThenFile($event)"
      @close="changeQuickMessagesBoxState(false)"
      @quickMessagesLoaded="setQuickMessages($event)"
      :bufferedMessages="quickMessagesBuffer"
      :opportunity="opportunity"
      :message="newMessage"
    />

    <div v-if="!checkPermissionInRoom()" class="input-box" style="word-wrap: break-word">
      Aviso: A sala atual pertence ao atendente {{ newestChat.user_name }}
    </div>

    <div v-else-if="reengagementRequired && !floatingOpenNewRoomState" class="input-box">
      <div class="responsible-avatar">
        <avatar
          :avatarLetter="$service.getFirstLetter(opportunity.name)"
          :hasBorder="true"
          :hasStatus="false"
          :size="2"
          avatarColor="#046AF3"
        />

        <div class="overflow-text">
          {{ opportunity.name }}
        </div>
      </div>

      <b-button
        v-if="!hasTemplateSendRequest"
        @click="changeFloatingOpenNewRoomState(true, preselectedBotForTemplate)"
        :disabled="hasRoomOpenRequest"
        class="reopen-button"
        variant="primary"
      >
        <span v-if="!hasRoomOpenRequest">Enviar template</span>

        <b-spinner v-else variant="light" small />
      </b-button>

      <b-button @click="changeFloatingOpenNewRoomState(true)" class="reopen-button" variant="primary">
        <div v-if="!hasTemplateSendRequest">Mudar Canal</div>

        <b-spinner v-else variant="light" small />
      </b-button>
    </div>

    <div v-else-if="isOpenRoom" v-show="!hasDraggable" class="input-box">
      <v-emoji-picker
        v-if="showEmojiSelection"
        @select="selectEmoji($event)"
        id="emojiSelector"
        class="emoji-selector"
      />

      <feather-icon
        v-if="!recording"
        @click="displayEmojiSelector()"
        id="emojiSelector"
        icon="SmileIcon"
        size="20"
        color="#14223B"
      />

      <textarea
        v-if="!recording"
        @keydown="checkUserInput($event)"
        @input="checkFormMobileInput($event), adjustTextareaHeight()"
        @keyup.enter="checkMobileEnter($event)"
        @paste="checkFilePaste"
        :style="{ height: textareaHeight }"
        v-model="newMessage"
        id="omnichannelNewMessage"
        type="text"
        placeholder="Mensagem..."
        autocomplete="off"
        ref="messageInput"
      />

      <i
        v-if="!recording && !isSendingFile"
        @click="showFileSourceSelector = !showFileSourceSelector"
        id="fileSourceTrigger"
        class="icon-clip"
      >
        <div
          v-if="showFileSourceSelector"
          v-click-outside="
            () => {
              showFileSourceSelector = false;
            }
          "
          class="file-source-popover"
        >
          <button class="popover-item" @click.stop="handleSelectNativeFile()">
            <Icon icon="mdi:paperclip" class="item-icon" />

            <div>Selecionar arquivo (Dispositivo)</div>
          </button>

          <button class="popover-item" @click.stop="handleOpenInternalRepository()">
            <Icon icon="mdi:folder-outline" class="item-icon" />

            <div>Abrir repositório interno</div>
          </button>

          <p style="margin: 6px 8px 2px; font-size: 11px; line-height: 1.3; color: #7d8797">
            {{ mediaLimitsHint }}
          </p>
        </div>
      </i>

      <div v-if="!recording && isSendingFile">
        <b-spinner style="color: #046af3" />
      </div>

      <input
        @change="sendMessage()"
        type="file"
        id="documentInput"
        style="display: none"
        multiple
        :accept="mediaAccept"
      />

      <div v-if="recording" class="audio-box">
        <feather-icon
          @click="clearAudio()"
          icon="Trash2Icon"
          size="22"
          style="cursor: pointer"
          color="#FF3E33"
        />

        <div class="sending-instruction">Clique para enviar</div>

        <div class="timer-recorder">
          <div class="green-ring" />

          <div>
            {{ formatTime(timer) }}
          </div>
        </div>
      </div>

      <div @click="getButtonActionByClick()" id="inputButton" class="rounded-enter">
        <i
          v-if="(newMessage && newMessage.length) || recording"
          class="icon-paper-plane"
          style="font-size: 19px; margin-left: 2px; margin-top: 1px; color: white"
        />

        <feather-icon v-else color="#ffffff" id="recordAudio" size="21" icon="MicIcon" />
      </div>

      <div
        v-if="!fromOmnichannelDesktop && hasAIPermission"
        @click="handleGenericModal('ai')"
        id="ai-assist-cta"
        class="ai-assist-cta"
      >
        <Icon icon="mdi:sparkles" class="ai-icon" />

        <b-tooltip target="ai-assist-cta" placement="top"> Gerar Insights com IA </b-tooltip>
      </div>
    </div>

    <div
      v-else-if="!isOpenRoom && newestChat && !openRoomFallback && !floatingOpenNewRoomState"
      class="input-box"
    >
      <div class="responsible-avatar">
        <avatar
          :avatarLetter="$service.getFirstLetter(opportunity.name)"
          :hasBorder="true"
          :hasStatus="false"
          :size="2"
          avatarColor="#046AF3"
        />

        <div class="overflow-text">
          {{ opportunity.name }}
        </div>
      </div>

      <b-button
        v-if="!hasTemplateSendRequest"
        @click="reopenImbotRoom()"
        :disabled="hasRoomOpenRequest"
        class="reopen-button"
        variant="primary"
      >
        <span v-if="!hasRoomOpenRequest">Reabrir Sala</span>

        <b-spinner v-else variant="light" small />
      </b-button>

      <b-button @click="changeFloatingOpenNewRoomState(true)" class="reopen-button" variant="primary">
        <div v-if="!hasTemplateSendRequest">Mudar Canal</div>

        <b-spinner v-else variant="light" small />
      </b-button>
    </div>

    <div v-else class="input-box" style="justify-content: flex-end">
      <textarea
        @keydown="checkUserInput($event)"
        @input="checkFormMobileInput($event), adjustTextareaHeight()"
        @keyup.enter="checkMobileEnter($event)"
        :style="{ height: textareaHeight }"
        id="openRoomNewMessage"
        type="text"
        placeholder="Mensagem..."
        autocomplete="off"
        style="width: calc(100% - 120px)"
        v-model="newMessage"
      />

      <b-button
        @click="changeFloatingOpenNewRoomState(true), changeQuickMessagesBoxState(false)"
        class="reopen-button"
        variant="primary"
      >
        <div>Abrir Sala</div>
      </b-button>
    </div>

    <b-modal v-if="templateData" id="bv-template_form" class="modal-dialog" hide-footer hide-header>
      <temp-form
        @send-template="sendTemplate"
        @closed="$bvModal.hide('bv-template_form')"
        :templateData="templateData"
        :opportunity-data="opportunity"
      />
    </b-modal>

    <InternalFileSelector
      v-if="showInternalFileSelector"
      @file-selected="sendMessageWithInternalFile($event)"
      @close="showInternalFileSelector = false"
    />
  </div>
</template>

<script>
import { BButton, BSpinner, BTooltip } from "bootstrap-vue";
import encoderPath from "opus-recorder/dist/encoderWorker.min.js";
import { Icon } from "@iconify/vue2";
import moment from "moment";
import Recorder from "opus-recorder";
import vClickOutside from "v-click-outside";
import { VEmojiPicker } from "v-emoji-picker";

import AIAssistent from "@/views/components/Omnichannel/AIAssistent.vue";
import Avatar from "@/views/components/Avatar.vue";
import FloatingOpenNewRoom from "./FloatingOpenNewRoom.vue";
import FloatingQuickMessages from "./FloatingQuickMessages.vue";
import InternalFileSelector from "./InternalFileSelector.vue";
import TemplateForm from "@/views/forms/TemplateForm.vue";
import {
  partitionMediaFiles,
  MEDIA_ACCEPT_ATTRIBUTE,
  getMediaLimitsSummary,
} from "@/services/mediaValidationService";

export default {
  directives: {
    clickOutside: vClickOutside.directive,
  },

  components: {
    BButton,
    BSpinner,
    BTooltip,
    Icon,
    VEmojiPicker,

    AIAssistent,
    Avatar,
    FloatingOpenNewRoom,
    FloatingQuickMessages,
    InternalFileSelector,
    "temp-form": TemplateForm,
  },

  props: {
    opportunity: {
      type: Object,
      required: true,
      default: function () {
        return {};
      },
    },

    newestChat: {
      required: true,
      default: function () {
        return {};
      },
    },

    fromOmnichannelDesktop: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      floatingQuickMessagesState: false,
      floatingOpenNewRoomState: false,

      showAIAssistent: false,
      currentGenericSemiModal: null,

      quickMessagesBuffer: null,

      templateData: null,

      lastKey: null,
      newMessage: "",
      textareaHeight: "24px",
      maxTextareaHeight: 100,
      filesArray: [],

      audioBlob: null,
      audioChunks: [],
      mediaRecorder: null,
      recording: false,

      timer: 0,
      timerLoop: null,

      startTime: null,
      endTime: null,
      touchTime: null,

      isSendingMessage: false,
      isSendingFile: false,

      showEmojiSelection: false,
      showFileSourceSelector: false,
      showInternalFileSelector: false,

      hasRoomOpenRequest: false,
      hasTemplateSendRequest: false,

      openRoomFallback: false,

      supportsAudioWorklet: false,

      templateModeBot: null,
    };
  },

  computed: {
    mediaAccept() {
      return MEDIA_ACCEPT_ATTRIBUTE;
    },

    mediaLimitsHint() {
      return getMediaLimitsSummary();
    },

    user() {
      return this.$store.getters["app/getUserData"];
    },

    role() {
      return this.$store.getters["app/getUserRole"];
    },

    isOpenRoom() {
      if (!this.newestChat) {
        return false;
      }

      return this.newestChat.closed_at === undefined || this.newestChat.closed_at === null;
    },

    quickMessages() {
      return this.$store.getters["omnichannel/getQuickMessages"];
    },

    hasDraggable() {
      return this.$store.getters["omnichannel/getIsHovering"];
    },

    hasAIPermission() {
      return this.$service.hasAIPermission();
    },

    showSemiModal() {
      return this.showAIAssistent;
    },

    expires_at() {
      if (!this.newestChat) return null;

      return this.newestChat.expires_at;
    },

    media_type() {
      if (!this.newestChat) return null;

      return this.newestChat.media_type;
    },

    already_expired() {
      if (!this.expires_at) return false;

      return moment().isAfter(moment(this.expires_at));
    },

    reengagementRequired() {
      if (this.media_type !== "WhatsappWaba") return false;

      return !this.expires_at || this.already_expired;
    },

    preselectedBotForTemplate() {
      if (this.reengagementRequired && this.newestChat.bot_id) {
        return { id: this.newestChat.bot_id, name: this.newestChat.bot_name };
      }

      return null;
    },
  },

  created() {
    this.$store.dispatch("omnichannel/changeHoveringState", false);

    this.$eventBus.on("websocket-suggestion-selected", (suggestion) => {
      this.handleWSSuggestion(suggestion);
    });

    this.preventMobileContextMenuOnElement();

    this.checkAudioWorkletSupport();
    this.createRecorder();
  },

  beforeDestroy() {
    this.$eventBus.off("websocket-suggestion-selected");
  },

  watch: {
    isOpenRoom() {
      if (this.isOpenRoom) {
        this.hasRoomOpenRequest = false;
      }
    },
  },

  methods: {
    handleSelectNativeFile() {
      document.getElementById("documentInput").click();

      this.showFileSourceSelector = false;
    },

    handleOpenInternalRepository() {
      this.showInternalFileSelector = true;

      this.showFileSourceSelector = false;
    },

    changeQuickMessagesBoxState(state) {
      this.floatingQuickMessagesState = state;
    },

    changeFloatingOpenNewRoomState(state, preselectedBot = null) {
      this.templateModeBot = preselectedBot;

      this.floatingOpenNewRoomState = state;
    },

    handleDragLeave() {
      this.$store.dispatch("omnichannel/changeHoveringState", false);
    },

    startTimer() {
      if (!this.timerLoop) {
        this.timer = 0;

        this.timerLoop = setInterval(this.updateTimer, 1000);
      }
    },

    stopTimer() {
      clearInterval(this.timerLoop);
      this.timerLoop = null;
    },

    updateTimer() {
      this.timer++;
    },

    preventMobileContextMenuOnElement() {
      const inputButton = document.getElementById("inputButton");

      if (inputButton) {
        inputButton.addEventListener("contextmenu", (event) => {
          event.preventDefault();
        });
      }
    },

    setQuickMessages(messages) {
      this.quickMessagesBuffer = messages;
    },

    checkPermissionInRoom() {
      if (!this.newestChat) {
        return true;
      }

      if (this.newestChat.closed_at != null && this.newestChat.closed_at != undefined) {
        return true;
      }

      if (this.newestChat.user_id != this.user.id && this.role == "salesRep") {
        return false;
      }

      return true;
    },

    hasParentWithId(element, parentId) {
      let currentElement = element;

      while (currentElement && currentElement !== document.body) {
        if (currentElement.id === parentId) {
          return true;
        }

        currentElement = currentElement.parentNode;
      }

      return false;
    },

    checkFilePaste(event) {
      if (event.clipboardData && event.clipboardData.items) {
        for (let i = 0; i < event.clipboardData.items.length; i++) {
          if (event.clipboardData.items[i].kind === "file") {
            const fileInput = document.getElementById("documentInput");
            const dropedFile = new DataTransfer();

            const file = event.clipboardData.items[i].getAsFile();
            const uniqueTimestamp = Date.now() + i;

            dropedFile.items.add(
              new File([file], file.name + uniqueTimestamp, {
                type: file.type,
              }),
            );

            fileInput.files = dropedFile.files;
            this.sendMessage();
          }
        }
      }
    },

    displayEmojiSelector() {
      this.showEmojiSelection = !this.showEmojiSelection;

      if (this.showEmojiSelection) {
        const clickHandler = (e) => {
          if (!this.hasParentWithId(e.target, "emojiSelector")) {
            this.showEmojiSelection = false;

            document.removeEventListener("click", clickHandler);
          }
        };

        document.addEventListener("click", clickHandler);
      }
    },

    selectEmoji(emoji) {
      this.newMessage += emoji.data;
    },

    async checkMicrophonePermission() {
      const permissionStatus = await navigator.permissions.query({
        name: "microphone",
      });

      return permissionStatus.state === "granted";
    },

    async requestMicrophonePermission() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        stream.getTracks().forEach((track) => track.stop());
      } catch (error) {
        this.$bvToast.toast("Algumas funções do sistema precisam de acesso ao microfone para funcionar!", {
          title: `Acesso ao microfone negado!`,
          autoHideDelay: 3000,
          variant: "danger",
          toaster: "b-toaster-top-left",
          solid: true,
        });
      }
    },

    showToastRequestingMicrophonePermission() {
      this.$bvToast.toast("Por favor, permita o acesso ao microfone para gravar áudio!", {
        title: `Permissão negada!`,
        autoHideDelay: 3000,
        variant: "warning",
        toaster: "b-toaster-top-left",
        solid: true,
      });

      this.requestMicrophonePermission();
    },

    async getButtonActionByClick() {
      if (this.newMessage && this.newMessage.length) {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
        }

        this.sendMessage();

        return;
      }

      if (await this.checkMicrophonePermission()) {
        this.recordAudio();
      } else {
        this.showToastRequestingMicrophonePermission();
      }
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);

      const remainingSeconds = seconds % 60;

      return `
      ${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}
      `;
    },

    adjustHeightAfterLineBreak() {
      const textarea =
        document.getElementById("omnichannelNewMessage") || document.getElementById("openRoomNewMessage");

      let height = textarea.scrollHeight;

      if (height > this.maxTextareaHeight) {
        height = this.maxTextareaHeight;
      }

      this.textareaHeight = height + 12 + "px";
    },

    adjustTextareaHeight() {
      const textarea =
        document.getElementById("omnichannelNewMessage") || document.getElementById("openRoomNewMessage");

      textarea.style.height = "24px";

      const newHeight = Math.min(textarea.scrollHeight, this.maxTextareaHeight);

      textarea.style.height = newHeight + "px";

      this.textareaHeight = newHeight + "px";
    },

    clearInputField() {
      this.filesArray = [];

      const documentInput = document.getElementById("documentInput");

      if (!documentInput) return;

      documentInput.value = "";

      if (documentInput.value) {
        documentInput.type = "text";
        documentInput.type = "file";
      }
    },

    checkForQuickMessages() {
      if (!this.newMessage) return;

      const matchMask = this.newMessage.match(/\/\w+/g);

      if (matchMask) {
        const command = matchMask[0];

        this.quickMessages.forEach((element) => {
          if (element.id == command) {
            this.newMessage = element.message;
          }
        });
      }

      this.changeQuickMessagesBoxState(false);
    },

    sendMessageThenFile(quickMessage) {
      this.changeQuickMessagesBoxState(false);

      this.newMessage = quickMessage.content;

      this.sendMessage();

      if (quickMessage.file_id && quickMessage.file) {
        this.sendMessageWithInternalFile(quickMessage.file);
      }
    },

    getQuickMessageAndCloseOptions(event) {
      const words = this.newMessage.split(" ");

      if (words.length > 1) {
        words[words.length - 1] = event.message;

        this.newMessage = words.join(" ");
      } else {
        this.newMessage = event.message;
      }

      this.changeQuickMessagesBoxState(false);

      const inputField =
        document.getElementById("omnichannelNewMessage") || document.getElementById("openRoomNewMessage");

      if (!inputField) return;

      inputField.focus();

      this.newMessage += " ";

      this.$nextTick(() => {
        const inputEvent = new Event("input", { bubbles: true });

        inputField.dispatchEvent(inputEvent);
      });
    },

    clearAudio() {
      this.timer = 0;
      this.recording = false;
      this.audioBlob = null;
      this.audioChunks = [];

      this.clearInputField();
      this.stopTimer();

      if (this.mediaRecorder && this.mediaRecorder.stream) {
        this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }

      this.mediaRecorder = null;
      this.createRecorder();
    },

    checkFormMobileInput(event) {
      if (window.innerWidth > 800) {
        return;
      }

      if ((event.inputType == "deleteContentBackward" && this.lastKey == "/") || event.data == " ") {
        this.checkForQuickMessages();
      }

      if (this.newMessage) {
        this.lastKey = this.newMessage.charAt(this.newMessage.length - 1);
      }

      if (event.data === "/") {
        this.changeQuickMessagesBoxState(true);
      }
    },

    checkMobileEnter(event) {
      if (window.innerWidth > 800) {
        return;
      }

      if (
        this.newMessage &&
        this.newMessage.length &&
        (event.key == "Enter" || event.keyCode === 13 || event.which === 13)
      ) {
        this.sendMessage();
      }
    },

    checkUserInput(event) {
      if (window.innerWidth < 800) {
        return;
      }

      if (event.key === "Enter" || event.keyCode === 13 || event.which === 13) {
        if (event.shiftKey) {
          const inputElement = event.target;
          const cursorPosition = inputElement.selectionStart;
          const inputValue = inputElement.value;

          this.newMessage =
            inputValue.substring(0, cursorPosition) + "\n" + inputValue.substring(cursorPosition);

          this.adjustHeightAfterLineBreak();

          inputElement.selectionStart = cursorPosition + 1;
          inputElement.selectionEnd = cursorPosition + 1;

          event.preventDefault();
        } else {
          event.preventDefault();

          if (this.newMessage && this.newMessage.length) {
            this.sendMessage();
          }
        }
      }

      if (event.key === "Backspace" || event.code === "Backspace" || event.keyCode === 8) {
        const inputElement = event.target;
        const inputValue = inputElement.value;
        const cursorPosition = inputElement.selectionStart;

        if (
          cursorPosition > 0 &&
          inputValue.charAt(cursorPosition - 1) === "/" &&
          inputValue.charAt(cursorPosition) === ""
        ) {
          this.changeQuickMessagesBoxState(false);
        }
      }

      if (event.key === "/" || event.code === "Slash" || event.keyCode === 191) {
        this.changeQuickMessagesBoxState(true);
      }

      if (event.key === " " || event.code === "Space" || event.keyCode === 32) {
        this.checkForQuickMessages();
      }
    },

    dropHandler(event) {
      event.preventDefault();

      const dataTransfer = event.dataTransfer;
      const files = dataTransfer.files;

      if (files.length > 0) {
        const fileInput = document.getElementById("documentInput");
        const dropedFile = new DataTransfer();

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const uniqueTimestamp = Date.now() + i;

          dropedFile.items.add(
            new File([file], file.name + uniqueTimestamp, {
              type: file.type,
            }),
          );
        }

        fileInput.files = dropedFile.files;
        this.sendMessage();
      }

      this.$store.dispatch("omnichannel/changeHoveringState", false);
    },

    checkAudioWorkletSupport() {
      try {
        this.supportsAudioWorklet = !!(
          (typeof AudioWorkletNode === "function" && window.AudioContext) ||
          window.webkitAudioContext
        );
      } catch (error) {
        this.supportsAudioWorklet = false;
      }
    },

    createRecorder() {
      try {
        const configs = {
          encoderPath,
          numberOfChannels: 1,
          encoderSampleRate: 48000,
          resampleQuality: 7,
          audioWorker: !this.supportsAudioWorklet,
        };

        try {
          this.mediaRecorder = new Recorder(configs);
          this.settingMediaRecorderEvents();
        } catch (workletError) {
          configs.audioWorker = true;
          this.mediaRecorder = new Recorder(configs);
          this.settingMediaRecorderEvents();
        }
      } catch (error) {
        this.$bvToast.toast(
          "Não foi possível inicializar o gravador de áudio. Verifique as permissões e tente novamente.",
          {
            title: `Erro no gravador de áudio!`,
            autoHideDelay: 3000,
            variant: "danger",
            toaster: "b-toaster-top-left",
            solid: true,
          },
        );
      }
    },

    settingMediaRecorderEvents() {
      this.mediaRecorder.onstart = () => {
        this.mediaRecorderOnStart();
      };

      this.mediaRecorder.ondataavailable = (data) => {
        this.mediaRecorderOnDataAvailable(data);
      };

      this.mediaRecorder.onstop = (data) => {
        this.mediaRecorderOnStop(data);
      };
    },

    mediaRecorderOnStart() {
      this.audioBlob = null;
      this.audioChunks = [];
    },

    mediaRecorderOnDataAvailable(data) {
      this.audioChunks.push(data);
    },

    showErrorToast() {
      this.$bvToast.toast(
        "Ocorreu um erro ao reabrir a sala! Verifique se já não existe outra mais recente aberta.",
        {
          title: `Sala não reaberta!`,
          autoHideDelay: 3000,
          variant: "danger",
          toaster: "b-toaster-top-left",
          solid: true,
        },
      );
    },

    handleWSSuggestion(suggestion) {
      if (!suggestion) return;

      this.newMessage = suggestion;

      if (this.$refs.messageInput) this.$refs.messageInput.focus();
    },

    handleGenericModal(from) {
      this.showAIAssistent = false;

      if (this.currentGenericSemiModal == from) {
        this.currentGenericSemiModal = null;

        return;
      }

      this.currentGenericSemiModal = from;

      const fromOptions = {
        ai: () => {
          this.showAIAssistent = true;
        },
      };

      if (fromOptions[from]) fromOptions[from]();
    },

    handleEmptyMessageOnNewRoom() {
      const newRoomMessageInput = document.getElementById("openRoomNewMessage");

      if (!newRoomMessageInput) return;

      newRoomMessageInput.focus();

      this.$bvToast.toast("Digite uma mensagem para abrir a sala!", {
        title: `Mensagem vazia!`,
        autoHideDelay: 3000,
        variant: "warning",
        toaster: "b-toaster-top-left",
        solid: true,
      });
    },

    reopenImbotRoom() {
      if (this.hasRoomOpenRequest) {
        return;
      }

      this.hasRoomOpenRequest = true;

      const chatData = {
        dialog_id: this.newestChat.id,
        opportunity_id: this.opportunity.id,
      };

      this.$store
        .dispatch("omnichannel/reopenChat", chatData)
        .then((response) => {
          if (response) this.$emit("roomReopened", response.data);
        })
        .catch((error) => {
          if (error.response?.message?.includes("WhatsApp Business")) {
            this.openRoomFallback = true;
          }

          this.hasRoomOpenRequest = false;
        });
    },

    mediaRecorderOnStop() {
      const elapsedTime = this.endTime - this.startTime;

      this.touchTime = elapsedTime / 1000;

      this.audioBlob = new Blob(this.audioChunks, {
        type: "audio/ogg",
      });

      const fileInput = document.getElementById("documentInput");
      const audioFileList = new DataTransfer();
      const uniqueTimestamp = Date.now();

      audioFileList.items.add(
        new File([this.audioBlob], "audio_" + uniqueTimestamp, {
          type: "audio/ogg",
        }),
      );

      fileInput.files = audioFileList.files;

      if (this.touchTime < 1) {
        this.clearAudio();
      } else {
        this.newMessage = null;

        this.sendMessage();
      }

      if (this.mediaRecorder && this.mediaRecorder.stream) {
        this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }

      this.mediaRecorder = null;
      this.createRecorder();
    },

    recordAudio() {
      try {
        if (!this.recording) {
          if (!this.mediaRecorder) {
            this.createRecorder();

            if (!this.mediaRecorder) return;
          }

          this.recording = true;
          this.startTime = new Date().getTime();

          try {
            this.mediaRecorder.start();
            this.startTimer();
          } catch (startError) {
            if (startError.message && startError.message.includes("encoder-worklet")) {
              this.recording = false;
              this.supportsAudioWorklet = false;
              this.createRecorder();

              if (this.mediaRecorder) {
                this.recording = true;
                this.mediaRecorder.start();
                this.startTimer();
              }
            } else {
              this.recording = false;
            }
          }
        } else {
          this.endTime = new Date().getTime();

          if (this.mediaRecorder) {
            this.mediaRecorder.stop();
          }

          this.stopTimer();
          this.recording = false;
        }
      } catch (error) {
        this.recording = false;
        this.stopTimer();

        this.$bvToast.toast(
          "Não foi possível utilizar o gravador de áudio. Recarregue a página e tente novamente ou entre em contato com o suporte.",
          {
            title: `Erro na gravação de áudio`,
            autoHideDelay: 3000,
            variant: "warning",
            toaster: "b-toaster-top-left",
            solid: true,
          },
        );
      }
    },

    selectTemplate(template) {
      this.templateData = template;

      this.$nextTick(() => {
        this.$bvModal.show("bv-template_form");
      });
    },

    getTemplateComponents(components) {
      if (!components?.length) return [];

      const componentsWithParams = [];

      components.forEach((element) => {
        if (element?.parameters?.length > 0) {
          componentsWithParams.push(element);
        }
      });

      return componentsWithParams;
    },

    selectBot(bot) {
      if (!this.newMessage || this.newMessage.length === 0) {
        this.handleEmptyMessageOnNewRoom();

        return;
      }

      this.changeFloatingOpenNewRoomState(false);

      const openRoomData = {
        bot_id: bot.id,
        bot_name: bot.name,
        message: this.newMessage,
        opportunity_id: this.opportunity.id,
        keep_bot_on_dialog: bot.keep_bot_on_dialog,
      };

      this.$store.dispatch("omnichannel/openChat", openRoomData).then((response) => {
        this.newMessage = null;
        this.$emit("roomOpened", response.data);
      });
    },

    sendTemplate(template) {
      this.changeFloatingOpenNewRoomState(false);
      this.hasTemplateSendRequest = true;

      const template_header = template.components.find((component) => component.type === "HEADER");
      const template_footer = template.components.find((component) => component.type === "FOOTER");
      const template_body = template.components.find((component) => component.type === "BODY");

      if (template_header) {
        template_header.type = "Header";

        delete template_header.example;
      }

      if (template_body) {
        template_body.type = "Body";

        delete template_body.example;
      }

      if (template_footer) {
        template_footer.type = "Footer";

        delete template_footer.example;
      }

      const header = template_header?.text || null;
      const footer = template_footer?.text || null;
      const body = template_body?.text || " ";

      const components = this.getTemplateComponents(template.components);

      const template_item = {
        name: template.name,
        language: template.language,
        components: components,
      };

      const templateOpenRoom = {
        bot_id: template.bot?.id,
        bot_name: template.bot?.name,
        opportunity_id: this.opportunity.id,
        keep_bot_on_dialog: template.bot?.keep_bot_on_dialog,
        media_type: "WhatsappWaba",

        header: header,
        footer: footer,
        message: body,

        template: template_item,
      };

      this.$store
        .dispatch("omnichannel/openChat", templateOpenRoom)
        .then((response) => {
          this.newMessage = null;

          this.$emit("roomOpened", response.data);
        })
        .finally(() => {
          this.hasTemplateSendRequest = false;
        });
    },

    sendMessage() {
      if (!this.isOpenRoom || this.reengagementRequired) return;

      const newMessage = this.newMessage;
      const now = moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ");
      const fileInput = document.getElementById("documentInput");

      this.timer = 0;
      this.newMessage = null;
      this.textareaHeight = "24px";

      const bodyObject = {
        dialog_id: this.newestChat.id,

        message_object: {
          message: newMessage || null,
          timestamp: now,
        },
      };

      if (fileInput.files.length > 0) {
        this.sendMessageWithFile(fileInput, bodyObject);

        return;
      }

      this.$store.dispatch("omnichannel/sendMessage", bodyObject).finally(() => {
        this.isSendingMessage = false;
      });
    },

    async sendMessageWithFile(fileInput, message) {
      const { validFiles, errors } = partitionMediaFiles(fileInput.files);

      if (errors.length) {
        this.$bvToast.toast(errors.join(" "), {
          title: "Arquivo não suportado",
          autoHideDelay: 5000,
          variant: "danger",
          toaster: "b-toaster-top-left",
          solid: true,
        });
      }

      if (!validFiles.length) {
        this.isSendingMessage = false;
        this.isSendingFile = false;

        this.clearInputField();

        return;
      }

      this.isSendingFile = true;

      const filePromises = [];

      for (const file of validFiles) {
        const promise = new Promise((resolve) => {
          const reader = new FileReader();
          const mimeType = file.type;
          let base64String;

          reader.onload = (event) => {
            base64String = event.target.result.split(",")[1];

            const fileData = {
              name: file.name,
              base64File: "data:" + mimeType + ";base64," + base64String,
            };

            this.filesArray.unshift(fileData);
            resolve();
          };

          reader.readAsDataURL(file);
        });

        filePromises.push(promise);
      }

      await Promise.all(filePromises);

      this.filesArray.reverse();

      if (this.filesArray.length > 15) {
        this.$bvToast.toast("Você só pode enviar até 15 arquivos por vez!", {
          title: `Limite de arquivos atingido!`,
          autoHideDelay: 3000,
          variant: "warning",
          toaster: "b-toaster-top-left",
          solid: true,
        });

        this.filesArray.splice(15);
      }

      for (const file of this.filesArray) {
        const messageObject = { ...message };

        messageObject.dialogId = this.opportunity.remote_id;
        messageObject.message_object.message = null;
        messageObject.message_object.file = file;

        await this.$store.dispatch("omnichannel/sendMessage", messageObject);
      }

      this.isSendingMessage = false;
      this.isSendingFile = false;

      this.clearInputField();
    },

    sendMessageWithInternalFile(files) {
      this.showInternalFileSelector = false;

      this.isSendingMessage = true;
      this.isSendingFile = true;

      if (!Array.isArray(files)) files = [files];

      files.forEach(async (file) => {
        const messageObject = {
          dialog_id: this.newestChat.id,
          message_object: {
            message: null,
            file: {
              id: file.id,
              name: file.name,
              mime: file.mime,
              url: file.url,
            },
            timestamp: moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
          },
        };

        await this.$store.dispatch("omnichannel/sendMessage", messageObject);
      });

      this.isSendingMessage = false;
      this.isSendingFile = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.white-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 10px;
  left: 0;
  bottom: 0px;
  width: 100%;
  background: #ffffff;

  .generic-semimodal-container {
    display: flex;
    position: absolute;
    height: 65dvh;
    width: 100%;
    left: 0;
    z-index: 1000;
    bottom: calc(100% + 1px);
    border-radius: 20px 20px 0px 0px;
    border: 1px solid #e5e5e5;
    box-shadow: -5px -5px 16px 0px #0000001a;
    background: #ffffff;
    overflow: hidden;
  }

  .input-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    width: calc(100% - 10px);
    padding: 7px 10px 7px 10px;
    border: solid 2px #cbced1;
    border-radius: 800px;
    background: #ffffff;
    gap: 8px;

    .reopen-button {
      font-size: 12px;
      white-space: nowrap;
      padding: 10px 20px;
      border-radius: 20px;
    }

    .responsible-avatar {
      display: flex;
      align-items: center;
      gap: 5px;
      max-width: 50%;
      overflow: hidden;

      .overflow-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }

    .emoji-selector {
      position: absolute;
      left: 0;
      bottom: 50px;
    }

    .icon-clip {
      position: static;
    }

    .file-source-popover {
      display: flex;
      flex-direction: column;
      position: absolute;
      left: 0;
      right: 0;
      bottom: calc(100% + 5px);
      z-index: 1000;
      padding: 8px;
      gap: 6px;
      border-radius: 10px;
      border: 1px solid #e5e5e5;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      background: #ffffff;

      .popover-item {
        display: flex;
        font-family: Poppins, Arial, serif !important;
        align-items: center;
        transition: background 0.15s ease;
        font-size: 14px;
        padding: 8px 10px;
        gap: 8px;
        border-radius: 8px;
        border: none;
        background: transparent;
        color: #14223b;
        cursor: pointer;

        &:hover {
          background: #f5f7fb;
        }

        .item-icon {
          font-size: 18px;
          min-width: 18px;
          min-height: 18px;
          color: #046af3;
        }
      }
    }

    .ai-assist-cta {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 33px;
      min-width: 33px;
      max-height: 33px;
      max-width: 33px;
      padding: 3px;
      border-radius: 100%;
      border: solid 2px #ffffff;
      box-shadow: 0px 0px 0px 2px #0461f3;
      background: #bdd9ff;
      cursor: pointer;

      .ai-icon {
        min-height: 25px;
        min-width: 25px;
        color: #046af3;
      }

      &:hover {
        background: #0461f3;

        .ai-icon {
          color: #ffffff;
        }
      }
    }

    .audio-box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 90%;

      .sending-instruction {
        font-size: 13px;
        padding: 5px 12px;
        border-radius: 20px;
        background: #f2f3f5;
        color: #8e8e8e;
      }

      .timer-recorder {
        display: flex;
        align-items: center;
        width: 70px;
        gap: 7px;
        color: #000000;

        .green-ring {
          height: 20px;
          width: 20px;
          border-radius: 100%;
          border: 4px solid #2ed47a;
        }
      }
    }

    .rounded-enter {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 7px;
      border-radius: 100%;
      background: #046af3;
      cursor: pointer;
    }

    i {
      font-size: 20px;
      cursor: pointer;
    }

    textarea {
      flex: 1;
      width: auto;
      border: none;
      resize: none;

      &::-webkit-scrollbar {
        width: 0px;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 13px;
        border: none;
        background-clip: content-box;
      }
    }

    textarea:focus,
    input:focus {
      outline: none;
    }

    input {
      flex: 1;
      width: auto;
      border: none;
    }

    .rounded-enter {
      margin-left: auto;
    }
  }

  @media (max-width: 800px) {
    padding-left: 5px;
    width: calc(100% - 15px);

    .input-box {
      .audio-box {
        width: 85%;
      }
    }
  }
}

.draggable-box {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  bottom: 0px;
  z-index: 100000;
  padding: 1rem;
  opacity: 0;
}

@media (max-width: 800px) {
  .white-box .input-box .file-source-popover {
    position: fixed;
    left: auto;
    width: 95vw;
    right: 51%;
    transform: translateX(50%);
    bottom: 65px;
    border-radius: 16px;
  }

  .white-box .input-box .file-source-popover .popover-item {
    font-size: 13px;
  }
}
</style>
